import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BatchReturn } from 'returnCenter-api';
import styled from 'styled-components';

import Button from 'components/ui/Button';
import Flex from 'components/ui/Flex';
import Text from 'components/ui/Text';
import { Dialog } from 'components/ui/Modal/Modal';
import useAppDispatch from 'hooks/useAppDispatch';
import useAppSelector from 'hooks/useAppSelector';
import { createBatchReturn, selectBatchDestinations } from 'store/reducers/batchReturnSlice';

type CreateBatchReturnModalProps = {
    open: boolean;
    onClose: () => void;
    onCreated: (batchReturn: BatchReturn) => void;
    preselectedDestinationId?: number;
};

const CreateBatchReturnModal = ({
    open,
    onClose,
    onCreated,
    preselectedDestinationId,
}: CreateBatchReturnModalProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const destinations = useAppSelector(selectBatchDestinations);

    const [selectedDestinationId, setSelectedDestinationId] = useState<number | null>(null);
    const [comment, setComment] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open) {
            setSelectedDestinationId(preselectedDestinationId ?? null);
            setComment('');
            setSubmitting(false);
        }
    }, [open, preselectedDestinationId]);

    const handleCreate = async () => {
        if (!selectedDestinationId) return;
        setSubmitting(true);
        try {
            const result = await dispatch(
                createBatchReturn({ batchDestinationId: selectedDestinationId, comment: comment || undefined }),
            ).unwrap();
            onCreated(result);
            onClose();
        } catch {
            setSubmitting(false);
        }
    };

    if (!open) return null;

    return (
        <Dialog
            onClose={onClose}
            header={t('batchReturn.createNewBatchReturn', 'Create Batch Return')}
            tint
            portal
            width="50%"
        >
            <Flex className="create-batch-return-modal-form p-3" $grow={1} column gap="1rem">
                <Flex column gap="0.25rem" className="destination-select mt-4">
                    <Label as="label" className="mt-2">
                        {t('batchReturn.destination', 'Destination')}
                    </Label>
                    <Select
                        value={selectedDestinationId ?? ''}
                        onChange={(e) => {
                            const val = e.target.value;
                            setSelectedDestinationId(val ? Number(val) : null);
                        }}
                    >
                        <option value="">{t('batchReturn.selectDestination', '-- Select destination --')}</option>
                        {destinations.map((dest) => (
                            <option key={dest.id} value={dest.id}>
                                {dest.name}
                            </option>
                        ))}
                    </Select>
                </Flex>

                <Flex column gap="0.25rem">
                    <Label as="label">{t('batchReturn.comment', 'Comment')}</Label>
                    <Input
                        type="text"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder={t('batchReturn.commentPlaceholder', 'Optional comment...')}
                    />
                </Flex>
            </Flex>

            <Flex justify="end" gap="0.5rem" className="button-row pt-0 pb-3 px-3">
                <Button rounded onClick={onClose} type="button">
                    {t('cancel')}
                </Button>
                <Button
                    rounded
                    main
                    onClick={handleCreate}
                    type="button"
                    disabled={!selectedDestinationId || submitting}
                >
                    {t('batchReturn.create', 'Create')}
                </Button>
            </Flex>
        </Dialog>
    );
};

export default CreateBatchReturnModal;

const Label = styled(Text)`
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${({ theme }) => theme.colors.textFaded};
`;

const Select = styled.select`
    padding: 0.5rem;
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 4px;
    font-size: 14px;
`;

const Input = styled.input`
    padding: 0.5rem;
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 4px;
    font-size: 14px;
`;
